import { useRef, useState } from "react";
import { GoPlus } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import UnderlineButton from "../../../components/buttons/UnderlineButton";
import colors from "../../../theme/color/ColorTheme";
import fontSize from "../../../theme/fonts/FontTheme";
import ProductImages from "./ProductImages";

const Wrapper = styled.div`
  padding: 0 40px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  @media (max-width: 950px) {
    flex-direction: column;
    padding: 0 20px;
  }
`;

const InfoWrapper = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  gap: 32px;
  height: fit-content;
  margin-left: 24px;
  box-sizing: border-box;
  flex-direction: column;

  @media (max-width: 950px) {
    margin-left: 0;
  }
`;

const Column = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ $gap }) => $gap || "4px"};
`;

const RowJustify = styled(Row)`
  justify-content: space-between;
`;

const Tag = styled.span`
  display: block;
  letter-spacing: 0.12rem;
  text-transform: uppercase;
  font-size: ${fontSize.xxs};
  color: ${colors.primaryGray};
`;

const Name = styled.span`
  letter-spacing: 0.12rem;
  color: ${colors.black};
  font-size: ${fontSize.m};
  text-transform: uppercase;
`;

const Price = styled(Name)`
  color: ${colors.maroon};
`;

const Text = styled.span`
  letter-spacing: 0.08rem;
  color: ${colors.black};
  font-size: ${fontSize.xs};
`;
const Title = styled(Text)`
  text-transform: ${({ $isUppercase }) =>
    $isUppercase ? "uppercase" : "capitalize"};
`;

const ColorSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const ColorBox = styled.button`
  width: 36px;
  height: 20px;
  background: ${({ $color }) => $color};
  border: none;
  cursor: pointer;
  padding: 0;
  box-sizing: border-box;

  ${({ $active, $color }) =>
    $active &&
    `
      box-shadow: inset 0 0 0 1.5px ${$color};
      background-clip: content-box;
      padding: 2px;
    `}
`;

const SizeWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
`;

const Sizes = styled.div`
  display: flex;
  gap: 16px;
`;

const Size = styled(Text)`
  cursor: pointer;
  pointer-events: auto;

  ${({ $active }) =>
    $active &&
    `  
      text-decoration: underline;
      text-underline-offset: 4px;
      text-decoration-thickness: 1px;
    `}
`;

const Description = styled.p`
  letter-spacing: 0.04rem;
  line-height: 1.24rem;
  color: ${colors.black};
  font-size: ${fontSize.xs};
  white-space: pre-line;
`;

const Accordion = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${colors.line};
`;

const IconWrapper = styled.div`
  svg {
    height: 18px;
    width: 18px;
    color: ${colors.black};
    transition: color 0.3s ease, transform 0.3s ease;
    transform: ${({ isOpen }) => (isOpen ? "rotate(90deg)" : "rotate(0deg)")};
  }
`;

const ToggleCard = styled.div`
  padding: 8px 0;
  cursor: pointer;
  border-bottom: 1px solid ${colors.line};
`;

const CardHeader = styled.div`
  display: flex;
  padding: 8px 4px;
  align-items: center;
  justify-content: space-between;

  span {
    letter-spacing: 0.1rem;
    font-size: ${fontSize.xs};
    text-transform: uppercase;
  }
`;

const CardBody = styled.div`
  margin: 0px 4px;
  overflow: hidden;
  max-height: ${({ maxHeight }) => `${maxHeight}px`};
  transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`;

function ProductDetails({ product }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeVariant = product.variants[activeIndex];
  const [activeSize, setActiveSize] = useState(product.variants[0].size[0]);

  const [openIndex, setOpenIndex] = useState(null);
  const [heights, setHeights] = useState([]);
  const cardRefs = useRef([]);

  const navigate = useNavigate();

  const toggleCard = (index) => {
    if (cardRefs.current[index]) {
      const newHeights = [...heights];
      newHeights[index] = cardRefs.current[index].scrollHeight;
      setHeights(newHeights);
    }
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <Wrapper>
      <ProductImages images={activeVariant.image} />

      <InfoWrapper>
        <Column>
          <Tag>COMING SOON</Tag>
          <RowJustify>
            <Name>{product.name}</Name>
            <Price>Pre - Launch</Price>
          </RowJustify>
        </Column>
        <Column>
          <Row>
            <Title>COLOR:</Title>
            <Text>{activeVariant.colorLabel}</Text>
          </Row>
          <ColorSelector>
            {product.variants.map((variant, index) => (
              <ColorBox
                key={index}
                $color={variant.color}
                $active={index === activeIndex}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveIndex(index);
                  setActiveSize((prevSize) =>
                    variant.size.includes(prevSize) ? prevSize : variant.size[0]
                  );
                }}
              />
            ))}
          </ColorSelector>
        </Column>
        <Column>
          <Title $isUppercase>{product.material}</Title>
          <Title>MADE IN NEPAL</Title>
        </Column>

        <SizeWrapper>
          <Sizes>
            <Title>SIZE:</Title>
            {activeVariant.size.map((size, i) => (
              <Size
                key={i}
                $active={size === activeSize}
                onClick={() => setActiveSize(size)}
              >
                {size}
              </Size>
            ))}
          </Sizes>
          <UnderlineButton placeholder={"SIZE CHART"} />
        </SizeWrapper>
        {/* <SecondaryButton
          title="CUSTOMIZE DESIGN"
          width="min(100%, 60%)"
          onClick={() => navigate("/measurement")}
        /> */}
        <PrimaryButton title="ADD TO WISHLIST" width="100%" />
        <Description>{product.description}</Description>

        <Accordion>
          {Object.keys(product.cardDetails).map((key, index) => (
            <ToggleCard key={index}>
              <CardHeader onClick={() => toggleCard(index)}>
                <span>{product.cardDetails[key].header}</span>
                <IconWrapper isOpen={openIndex === index}>
                  <GoPlus />
                </IconWrapper>
              </CardHeader>
              <CardBody
                maxHeight={openIndex === index ? heights[index] || 0 : 0}
              >
                <Description ref={(el) => (cardRefs.current[index] = el)}>
                  {product.cardDetails[key].description}
                </Description>
              </CardBody>
            </ToggleCard>
          ))}
        </Accordion>
      </InfoWrapper>
    </Wrapper>
  );
}

export default ProductDetails;
